import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { toastr } from 'react-redux-toastr'
import { Link } from 'react-router-dom'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Nav, NavItem, NavLink, TabContent, TabPane } from '@/components/ui-compat'
import { cn } from '@/lib/utils'
import {
  ArrowLeft,
  AlertTriangle,
  Send,
  Trash2,
  ClipboardCheck,
  FilePlus2,
  RotateCcw,
  Undo2,
  FolderInput,
  CheckCheck,
  Ban,
  Copy,
  MoreVertical,
  LoaderCircle,
  Info,
  History,
} from 'lucide-react'
import { submittalStatusVariant } from '@/lib/status-variants'

import * as Query from '../../../api/gql/query'
import * as Mutation from '../../../api/gql/mutation'
import { useRouter, useTabSwitch } from '../../../hooks'
import { useModuleActions } from '../../../hooks/useMyPermissions'
import { contractRefLabel } from '../../cost-control/lib/integrations'

import SubmittalDetailSkeleton from './SubmittalDetailSkeleton'
import SubmittalFieldsCard from './SubmittalFieldsCard'
import SubmittalAttachmentsCard from './SubmittalAttachmentsCard'
import SubmittalReviewsCard from './SubmittalReviewsCard'
import SubmittalActivityCard from './SubmittalActivityCard'
import SubmitForReviewDialog from './SubmitForReviewDialog'
import ConsolidateDialog from './ConsolidateDialog'
import ReopenDialog from './ReopenDialog'
import PublishDialog from './PublishDialog'
import SubmittalCreateDialog from '../components/SubmittalCreateDialog'

// UI-only policy, not a mirror of a backend status gate: updateSubmittal has no
// DRAFT/RETURNED_FOR_REVISION check of its own (it only special-cases
// CLOSED/VOID, requiring ADMIN there; assertSubmittalEditable itself is a pure
// permission check with no status condition). Restricted to these two statuses
// per the brief's explicit instruction for the Edit button. Attachments are
// narrower still — DRAFT only, and only on the current revision — because
// assertRevisionAttachable in src/graphql/submittals/attachment.ts DOES
// enforce that at the backend.
const FIELDS_EDITABLE_STATUSES = ['DRAFT', 'RETURNED_FOR_REVISION']

// Mirrors REOPENABLE_STATUSES in src/graphql/submittals/lifecycle.ts — the backend decides.
const REOPENABLE_STATUSES = ['APPROVED', 'APPROVED_AS_NOTED', 'RETURNED_FOR_REVISION', 'REJECTED', 'CLOSED']

/**
 * Plain "confirm, then fire one mutation" lifecycle actions — everything that
 * doesn't need its own form state (verdict/note/folder for Consolidate,
 * reviewer selection for Submit-for-review, which stay as their own dialog
 * components below). Kept as one dialog + one lookup table instead of five
 * near-identical components.
 */
type ConfirmActionKind = 'delete' | 'newRevision' | 'withdraw' | 'close' | 'void'

const CONFIRM_ACTION_CONFIG: Record<
  ConfirmActionKind,
  { titleKey: string; bodyKey: string; confirmLabelKey: string; variant: 'default' | 'destructive'; Icon: any }
> = {
  delete: {
    titleKey: 'Submittals_delete_confirm_title',
    bodyKey: 'Submittals_delete_confirm_body',
    confirmLabelKey: 'Common_delete',
    variant: 'destructive',
    Icon: Trash2,
  },
  newRevision: {
    titleKey: 'Submittals_new_revision_confirm_title',
    bodyKey: 'Submittals_new_revision_confirm_body',
    confirmLabelKey: 'Submittals_new_revision_btn',
    variant: 'default',
    Icon: FilePlus2,
  },
  withdraw: {
    titleKey: 'Submittals_withdraw_confirm_title',
    bodyKey: 'Submittals_withdraw_confirm_body',
    confirmLabelKey: 'Submittals_withdraw_btn',
    variant: 'default',
    Icon: Undo2,
  },
  close: {
    titleKey: 'Submittals_close_confirm_title',
    bodyKey: 'Submittals_close_confirm_body',
    confirmLabelKey: 'Submittals_close_btn',
    variant: 'default',
    Icon: CheckCheck,
  },
  void: {
    titleKey: 'Submittals_void_confirm_title',
    bodyKey: 'Submittals_void_confirm_body',
    confirmLabelKey: 'Submittals_void_btn',
    variant: 'destructive',
    Icon: Ban,
  },
}

interface ConfirmActionDialogProps {
  kind: ConfirmActionKind
  submitting: boolean
  onCancel: () => void
  onConfirm: () => void
}

const ConfirmActionDialog: React.FC<ConfirmActionDialogProps> = ({ kind, submitting, onCancel, onConfirm }) => {
  const { t } = useTranslation()
  const config = CONFIRM_ACTION_CONFIG[kind]
  const { Icon } = config

  return (
    <Dialog open onOpenChange={(next) => { if (!next && !submitting) onCancel() }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t(config.titleKey)}</DialogTitle>
          <DialogDescription>{t(config.bodyKey)}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="outline" onClick={onCancel} disabled={submitting}>
            {t('Common_cancel')}
          </Button>
          <Button variant={config.variant} onClick={onConfirm} disabled={submitting}>
            {submitting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Icon className="h-4 w-4" />}
            {t(config.confirmLabelKey)}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

const SubmittalDetail: React.FC = () => {
  const { t } = useTranslation()
  const { match, history } = useRouter()
  const { projectId, submittalId } = match.params as { projectId: string; submittalId: string }

  const { data: meData } = useQuery(Query.ME)
  const meId = meData?.me?.id

  const { canCreate, canEdit, canDelete, canApprove, canAdmin } = useModuleActions('SUBMITTALS', projectId)
  const [tab, setTab] = useTabSwitch('detail')

  const { data, loading, error, refetch } = useQuery(Query.SUBMITTAL, {
    variables: { id: submittalId },
    skip: !submittalId,
    fetchPolicy: 'cache-and-network',
  })

  const submittal = data?.submittal

  // Revision switcher: owned here (not inside SubmittalAttachmentsCard) so
  // SubmittalReviewsCard can follow the exact same selection — a review list
  // that silently disagreed with the attachments list about which revision
  // is "displayed" would be confusing on any submittal with more than one
  // revision.
  const [selectedRevisionId, setSelectedRevisionId] = useState<string | null>(null)

  const [submitDialogOpen, setSubmitDialogOpen] = useState(false)
  const [consolidateDialogOpen, setConsolidateDialogOpen] = useState(false)
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false)
  const [publishDialogOpen, setPublishDialogOpen] = useState(false)
  const [copyDialogOpen, setCopyDialogOpen] = useState(false)
  const [confirmAction, setConfirmAction] = useState<ConfirmActionKind | null>(null)

  const [deleteSubmittal, { loading: deleting }] = useMutation(Mutation.DELETE_SUBMITTAL)
  const [createRevision, { loading: creatingRevision }] = useMutation(Mutation.CREATE_SUBMITTAL_REVISION)
  const [closeSubmittal, { loading: closing }] = useMutation(Mutation.CLOSE_SUBMITTAL)
  const [voidSubmittal, { loading: voiding }] = useMutation(Mutation.VOID_SUBMITTAL)
  const [withdrawSubmittal, { loading: withdrawing }] = useMutation(Mutation.WITHDRAW_SUBMITTAL)

  const handleBack = () => history.push(`/project/${projectId}/submittals`)

  if (loading && !submittal) return <SubmittalDetailSkeleton />

  if (error || !submittal) {
    return (
      <div className="submittals-module">
        <Button variant="ghost" size="sm" onClick={handleBack} className="mb-4 -ml-2">
          <ArrowLeft className="h-4 w-4" />
          {t('Submittals_back_to_list')}
        </Button>
        <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
          <AlertTriangle className="h-10 w-10 mb-3 opacity-40" />
          <p className="text-sm">{error ? error.message : t('Submittals_not_found')}</p>
        </div>
      </div>
    )
  }

  const isCreator = submittal.createdBy?.id === meId
  const userMayEdit = (isCreator && canCreate) || canEdit || canAdmin
  const fieldsEditable = FIELDS_EDITABLE_STATUSES.includes(submittal.status) && userMayEdit
  // Attachments are narrower than fields: DRAFT only, per assertRevisionAttachable.
  // Whether the SELECTED revision is the current one (attachments on older
  // revisions are always read-only) is still decided inside
  // SubmittalAttachmentsCard — only the selectedRevisionId state itself lives
  // here now, shared with SubmittalReviewsCard below.
  const attachmentsEditable = submittal.status === 'DRAFT' && userMayEdit
  // Mirrors deleteSubmittal's own gate exactly (src/graphql/submittals/submittal.ts):
  // creator-with-CREATE, or DELETE — NOT ADMIN, which that resolver never checks.
  const canDeleteSubmittal = (isCreator && canCreate) || canDelete

  const ownerNames: string = (submittal.currentOwners || []).map((u: any) => u.name).join(', ')

  const revisions: any[] = submittal.revisions || []
  const displayedRevisionId = selectedRevisionId || submittal.currentRevision.id
  const displayedRevision = revisions.find((r) => r.id === displayedRevisionId) || submittal.currentRevision
  // SubmittalRowFragment (spread into SubmittalDetailFragment) only selects
  // `currentRevision { id index }` — publishedAt isn't on that shallow object.
  // The full row lives in `revisions` (SubmittalDetailFragment selects every
  // scalar there), so look it up by id instead of adding a second field
  // selection for `currentRevision` to the shared fragment. Deliberately NOT
  // reusing `displayedRevision`: publish eligibility is about the submittal's
  // actual current revision, not whichever older one the user happens to be
  // browsing in the cards below.
  const currentRevisionFull = revisions.find((r) => r.id === submittal.currentRevision.id) || submittal.currentRevision

  const isNonFinalStatus = !['CLOSED', 'VOID'].includes(submittal.status)
  const canPublishNow = canApprove && !currentRevisionFull?.publishedAt

  const confirmSubmitting = deleting || creatingRevision || closing || voiding || withdrawing

  const handleConfirmedAction = async () => {
    try {
      if (confirmAction === 'delete') {
        await deleteSubmittal({ variables: { id: submittal.id } })
        toastr.success('', t('Submittals_deleted'))
        setConfirmAction(null)
        handleBack()
        return
      }
      if (confirmAction === 'newRevision') {
        await createRevision({ variables: { id: submittal.id } })
        toastr.success('', t('Submittals_new_revision_created'))
      } else if (confirmAction === 'withdraw') {
        await withdrawSubmittal({ variables: { id: submittal.id } })
        toastr.success('', t('Submittals_withdrawn'))
      } else if (confirmAction === 'close') {
        await closeSubmittal({ variables: { id: submittal.id } })
        toastr.success('', t('Submittals_closed'))
      } else if (confirmAction === 'void') {
        await voidSubmittal({ variables: { id: submittal.id } })
        toastr.success('', t('Submittals_voided'))
      }
      refetch()
      setConfirmAction(null)
    } catch (submitError: any) {
      toastr.error(t('Common_error'), submitError.message)
    }
  }

  return (
    <div className="submittals-module space-y-4">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1.5 flex-wrap">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 shrink-0"
            onClick={handleBack}
            title={t('Submittals_back_to_list')}
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <span className="text-xs font-mono text-muted-foreground shrink-0">{submittal.displayNumber}</span>
          <h1 className="text-xl font-bold flex-1 truncate mb-0">{submittal.title}</h1>
          <Badge variant={submittalStatusVariant(submittal.status)} className="font-medium whitespace-nowrap shrink-0">
            {t(`Submittal_status_${submittal.status}`)}
          </Badge>
          {submittal.currentRevision && (
            <Badge variant="secondary" className="font-mono whitespace-nowrap shrink-0">
              {t('Submittals_rev_chip', { index: submittal.currentRevision.index })}
            </Badge>
          )}
          {submittal.costContract && (
            <Link to={`/project/${projectId}/cost-control/contracts/${submittal.costContract.id}`} className="shrink-0">
              <Badge variant="secondary" className="whitespace-nowrap">
                {t('CostControl_submittal_contract_badge', { label: contractRefLabel(submittal.costContract) })}
              </Badge>
            </Link>
          )}

          <div className="flex items-center gap-2 shrink-0">
            {canCreate && (
              <Button variant="outline" size="sm" onClick={() => setCopyDialogOpen(true)}>
                <Copy className="h-4 w-4" />
                {t('Submittals_copy_btn')}
              </Button>
            )}

            {submittal.status === 'DRAFT' && (
              <>
                {canDeleteSubmittal && (
                  <Button variant="destructive" size="sm" onClick={() => setConfirmAction('delete')}>
                    <Trash2 className="h-4 w-4" />
                    {t('Common_delete')}
                  </Button>
                )}
                {userMayEdit && (
                  <Button size="sm" onClick={() => setSubmitDialogOpen(true)}>
                    <Send className="h-4 w-4" />
                    {t('Submittals_submit_for_review_btn')}
                  </Button>
                )}
              </>
            )}

            {submittal.status === 'OPEN' && userMayEdit && (
              <Button variant="outline" size="sm" onClick={() => setConfirmAction('withdraw')}>
                <Undo2 className="h-4 w-4" />
                {t('Submittals_withdraw_btn')}
              </Button>
            )}

            {['OPEN', 'UNDER_REVIEW'].includes(submittal.status) && canApprove && (
              <Button size="sm" onClick={() => setConsolidateDialogOpen(true)}>
                <ClipboardCheck className="h-4 w-4" />
                {t('Submittals_consolidate_btn')}
              </Button>
            )}

            {submittal.status === 'RETURNED_FOR_REVISION' && userMayEdit && (
              <Button size="sm" onClick={() => setConfirmAction('newRevision')}>
                <FilePlus2 className="h-4 w-4" />
                {t('Submittals_new_revision_btn')}
              </Button>
            )}

            {submittal.status === 'REJECTED' && canApprove && (
              <Button variant="outline" size="sm" onClick={() => setConfirmAction('close')}>
                <CheckCheck className="h-4 w-4" />
                {t('Submittals_close_btn')}
              </Button>
            )}

            {['APPROVED', 'APPROVED_AS_NOTED'].includes(submittal.status) && (
              <>
                {canPublishNow && (
                  <Button size="sm" onClick={() => setPublishDialogOpen(true)}>
                    <FolderInput className="h-4 w-4" />
                    {t('Submittals_publish_btn')}
                  </Button>
                )}
                {canApprove && (
                  <Button variant="outline" size="sm" onClick={() => setConfirmAction('close')}>
                    <CheckCheck className="h-4 w-4" />
                    {t('Submittals_close_btn')}
                  </Button>
                )}
              </>
            )}

            {REOPENABLE_STATUSES.includes(submittal.status) && canAdmin && (
              <Button variant="outline" size="sm" onClick={() => setReopenDialogOpen(true)}>
                <RotateCcw className="h-4 w-4" />
                {t('Submittals_reopen_btn')}
              </Button>
            )}

            {isNonFinalStatus && canAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" title={t('Common_more')}>
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onSelect={() => setConfirmAction('void')}
                  >
                    <Ban className="h-4 w-4 mr-2" />
                    {t('Submittals_void_btn')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
        {!!ownerNames && (
          <p className="text-sm text-muted-foreground pl-10 mb-0 truncate">
            {t('Submittal_current_owner')}: {ownerNames}
          </p>
        )}
      </div>

      <div className="tab">
        <Nav tabs>
          <NavItem>
            <NavLink className={cn('flex items-center', { active: tab === 'detail' })} onClick={() => setTab('detail')}>
              <Info className="h-4 w-4 mr-2" />
              {t('Submittals_detail_tab_detail')}
            </NavLink>
          </NavItem>
          <NavItem>
            <NavLink
              className={cn('flex items-center', { active: tab === 'activity' })}
              onClick={() => setTab('activity')}
            >
              <History className="h-4 w-4 mr-2" />
              {t('Submittals_detail_tab_activity')}
            </NavLink>
          </NavItem>
        </Nav>

        <TabContent activeTab={tab}>
          <TabPane tabId="detail" className="border-0 bg-transparent p-0 shadow-none">
            <div className="space-y-4">
              <SubmittalFieldsCard
                submittal={submittal}
                editable={fieldsEditable}
                projectId={projectId}
                refetch={refetch}
              />

              <SubmittalAttachmentsCard
                submittal={submittal}
                revision={submittal.currentRevision}
                editable={attachmentsEditable}
                refetch={refetch}
                selectedRevisionId={displayedRevisionId}
                onSelectRevisionChange={setSelectedRevisionId}
              />

              <SubmittalReviewsCard
                submittal={submittal}
                revision={displayedRevision}
                meId={meId}
                refetch={refetch}
              />
            </div>
          </TabPane>

          <TabPane tabId="activity" className="border-0 bg-transparent p-0 shadow-none">
            <SubmittalActivityCard submittalId={submittal.id} />
          </TabPane>
        </TabContent>
      </div>

      {/* Copy to new: the plain create dialog, prefilled from this submittal (Freelo #32319047).
          Mounted only while open so each copy starts from the current field values. */}
      {copyDialogOpen && (
        <SubmittalCreateDialog
          open={copyDialogOpen}
          onOpenChange={setCopyDialogOpen}
          projectId={projectId}
          source={submittal}
        />
      )}

      {submitDialogOpen && (
        <SubmitForReviewDialog
          submittalId={submittal.id}
          projectId={projectId}
          open={submitDialogOpen}
          onOpenChange={setSubmitDialogOpen}
          refetch={refetch}
          latestRevision={currentRevisionFull}
        />
      )}

      {consolidateDialogOpen && (
        <ConsolidateDialog
          submittalId={submittal.id}
          projectId={projectId}
          open={consolidateDialogOpen}
          onOpenChange={setConsolidateDialogOpen}
          refetch={refetch}
          defaultFolderId={currentRevisionFull?.workflowPlan?.targetFolderId ?? null}
        />
      )}

      {reopenDialogOpen && (
        <ReopenDialog
          submittalId={submittal.id}
          open={reopenDialogOpen}
          onOpenChange={setReopenDialogOpen}
          refetch={refetch}
          published={!!currentRevisionFull?.publishedAt}
        />
      )}

      {publishDialogOpen && (
        <PublishDialog
          submittalId={submittal.id}
          projectId={projectId}
          open={publishDialogOpen}
          onOpenChange={setPublishDialogOpen}
          refetch={refetch}
          defaultFolderId={currentRevisionFull?.workflowPlan?.targetFolderId ?? null}
        />
      )}

      {confirmAction && (
        <ConfirmActionDialog
          kind={confirmAction}
          submitting={confirmSubmitting}
          onCancel={() => setConfirmAction(null)}
          onConfirm={handleConfirmedAction}
        />
      )}
    </div>
  )
}

export default SubmittalDetail
